import { BlobProvider } from '@react-pdf/renderer';
import { format } from 'date-fns/format';
import { FileText } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDatePicker from '../../components/GenericDatePicker';
import GenericDropdown from '../../components/GenericDropDown';
import GenericReportPDF from '../../components/GenericReportPDF';
import GenericTable from '../../components/GenericTable';
import { useAuth } from '../../context/AuthContext';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { GameConfig } from '../../models/GameConfig';
import { User } from '../../models/Users';
import { getAllActiveGameConfigs } from '../../redux/entities/gameEntity';
import { getDayTransactions } from '../../redux/entities/transactionEntity';
import { getAllUsers } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Role } from '../../utils/rolesUtil';

const DownloadButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  
  &:hover {
    background-color: #357abd;
  }
`;

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const TableWrapper = styled.div`
  overflow: visible;
  margin-bottom: 2rem;
`;

const ReportTableSection = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Day2DayReport: React.FC = () => {

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedResultTime, setSelectedResultTime] = useState<any>('');
  const [selectedGameType, setSelectedGameType] = useState<string>('');
  const [resultTimeOptions, setResultTimeOptions] = useState<any[]>([]);

  const [selectedAdmin, setSelectedAdmin] = useState<any>();
  const [selectedGame, setSelectedGame] = useState<any>();
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [selectedCustomer, setSelectedCustomer] = useState<any>();
  const { user, role } = useAuth(); // user is from context

  // Helper function to determine visible commission columns based on role
  const getVisibleCommissionColumns = () => {
    const baseColumns = [
      {
        header: 'Date',
        field: 'date',
        render: (row: any) => {
          const date = row.date ? new Date(row.date) : new Date();
          return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'dd-MM-yyyy');
        }
      },
      {
        header: 'Customer',
        field: 'customerName',
        render: (row: any) => row.customerName || 'N/A'
      },
      {
        header: 'Customer Phone',
        field: 'customerPhone',
        render: (row: any) => row.customerPhone || 'N/A'
      }
    ];

    // Add Agent column for Admin and Agent roles
    if (user?.role === 'Admin' || user?.role === 'Agent') {
      baseColumns.push({
        header: 'Agent',
        field: 'agentName',
        render: (row: any) => row.agentName || 'N/A'
      });
    }

    // Add Admin column only for Admin role
    if (user?.role === 'Admin') {
      baseColumns.push({
        header: 'Admin',
        field: 'adminName',
        render: (row: any) => row.adminName || 'N/A'
      });
    }

    // Add remaining common columns
    const commonColumns = [
      {
        header: 'Game',
        field: 'game',
        render: (row: any) => row.game || 'N/A'
      },
      {
        header: 'Result Time',
        field: 'resultTime',
        render: (row: any) => row.resultTime || 'N/A'
      },
      {
        header: 'Transaction Amount',
        field: 'transactionPoint',
        render: (row: any) => Number(row.transactionPoint || 0).toFixed(2)
      },
      {
        header: 'Win Amount',
        field: 'winPoint',
        render: (row: any) => Number(row.winPoint || 0).toFixed(2)
      },
      {
        header: 'Profit/Loss',
        field: 'profit',
        render: (row: any) => {
          const profit = (row.winPoint || 0) - (row.transactionPoint || 0);
          return Number(profit).toFixed(2);
        }
      }
    ];

    const commissionColumns = [];

    // Add customer commission for customer role
    if (role === Role.Customer || role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) {
      commissionColumns.push(
        { header: 'Customer Commission', field: 'customerCommission', render: (row: any) => Number(row.customerCommission).toFixed(2) }
      );
    }

    // Add agent commission for agent and above roles
    if (role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) {
      commissionColumns.push(
        { header: 'Agent Commission', field: 'agentCommission', render: (row: any) => Number(row.agentCommission).toFixed(2) }
      );
    }

    // Add admin commission for admin and super admin roles
    if (role === Role.Admin || role === Role.SuperAdmin) {
      commissionColumns.push(
        { header: 'Admin Commission', field: 'adminCommission', render: (row: any) => Number(row.adminCommission).toFixed(2) }
      );
    }

    // Add Total column that sums up all visible commissions
    commissionColumns.push({
      header: 'Total',
      field: 'totalCommission',
      render: (row: any) => {
        let total = 0;

        // Add winPoint if visible (assuming it's always visible based on the baseColumns)
        total += Number(row.winPoint - row.transactionPoint) || 0;

        // Add customer commission if visible
        if (role === Role.Customer || role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) {
          total += Number(row.customerCommission) || 0;
        }

        // Add agent commission if visible
        if (role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) {
          total += Number(row.agentCommission) || 0;
        }

        // Add admin commission if visible
        if (role === Role.Admin || role === Role.SuperAdmin) {
          total += Number(row.adminCommission) || 0;
        }

        return total.toFixed(2);
      }
    });

    return [...baseColumns, ...commonColumns, ...commissionColumns];
  };

  const dispatch = useAppDispatch();
  const { showModalStrip } = useModalStrip();
  const rawUsers = useAppSelector((state) => state.entities.userReducer.users);
  const users = useMemo(() => (Array.isArray(rawUsers) ? rawUsers : []), [rawUsers]);
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.activeGameConfigs);
  const aggTran = useAppSelector((state) => state.entities.transactionReducer.aggTran);

  const [gameTypeOptions] = useState<any[]>([
    { label: 'Single', value: 'Single' },
    { label: 'Patti', value: 'Patti' },
    { label: 'Digit', value: 'Digit' },
  ]);

  useEffect(() => {
    const fetchGameConfigs = async () => {
      if (!gameConfigs || gameConfigs.length === 0) {
        try {
          await dispatch(getAllActiveGameConfigs()).unwrap();
        } catch (err) {
          showModalStrip('danger', 'Failed to load game configurations', 5000);
        }
      }
    };

    fetchGameConfigs();
  }, [dispatch, gameConfigs, showModalStrip]);

  // useEffect(() => {
  //   if (user) {
  //     const userId = user.id?.toString();
  //     if (role === Role.Customer) {
  //       setSelectedCustomer(user);
  //     }
  //     else if (role === Role.Agent) {
  //       setSelectedAgent(user);
  //     }
  //     else if (role === Role.Admin) {
  //       setSelectedAdmin(user);
  //     }
  //   }

  // }, [user, role]);

  // Group gameConfigs by game name
  const groupedGameConfigs = useMemo(
    () =>
      gameConfigs.reduce((acc, config) => {
        const gameName = config?.game?.name;
        if (!gameName) return acc;
        if (!acc[gameName]) {
          acc[gameName] = [];
        }
        acc[gameName].push(config);
        return acc;
      }, {} as Record<string, GameConfig[]>),
    [gameConfigs]
  );

  useEffect(() => {
    dispatch(getAllUsers(0));
  }, [dispatch]);

  // Get the resultTimes based on selected game
  const getResultTimes = useCallback(
    (gameName: string) => {
      const selectedGameConfig = groupedGameConfigs[gameName] || [];
      return selectedGameConfig.map((config: any) => config.resultTime);
    },
    [groupedGameConfigs]
  );

  useEffect(() => {
    if (selectedGame === '0') {
      setResultTimeOptions([{ value: '0', label: 'Select All' }]);
    } else if (selectedGame !== '0') {
      const resultTimes = getResultTimes(selectedGame).map(resultTime => ({
        value: resultTime.id.toString(),
        label: resultTime.resultTime,
      }));

      // Sorting the times based on the label (which is in 12-hour format like "10:30 AM")
      const sortedResultTimes = resultTimes.sort((a, b) => {
        const timeA = new Date(`01/01/2020 ${a.label}`); // Adding a fixed date to parse the time correctly
        const timeB = new Date(`01/01/2020 ${b.label}`);
        return timeA.getTime() - timeB.getTime();
      });

      setResultTimeOptions(sortedResultTimes);
    }
  }, [getResultTimes, selectedGame]);


  const handleFetchTransactions = () => {
    if (!startDate && !endDate && !selectedGame && !selectedResultTime
      && (!selectedAdmin || !selectedAgent || !selectedCustomer)) {
      alert("Required fields are missing");
    }

    dispatch(getDayTransactions({
      startDate: format(startDate === null ? new Date() : startDate, "yyyy-MM-dd"),
      endDate: format(endDate === null ? new Date() : endDate, "yyyy-MM-dd"),
      gameId: groupedGameConfigs[selectedGame] && selectedGame?.id !== '0' ? groupedGameConfigs[selectedGame][0]?.game?.id : undefined,
      custId: selectedCustomer?.id !== '0' ? selectedCustomer?.id : undefined,
      agentId: selectedAgent?.id !== '0' ? selectedAgent?.id : undefined,
      adminId: selectedAdmin?.id !== '0' ? selectedAdmin?.id : undefined,
      resultTimeId: selectedResultTime !== '' && selectedResultTime !== '0' ? selectedResultTime : undefined,
      gameType: selectedGameType !== '' ? selectedGameType : undefined,
    }));
  }

  // Directly filter users based on role
  const getFilteredUsers = (parent: string, parentUser: User | null) => {
    if (parent === Role.Admin) {
      return users.filter((user) => user && user.role === Role.Admin);
    }
    if (parent === Role.Agent) {
      return parentUser ? (parentUser.children ? parentUser.children : []) : users.filter((user) => user && user.role === Role.Agent);
    }
    if (parent === Role.Customer) {
      const customers = parentUser ? (parentUser.children ? parentUser.children : []) : users.filter((user) => user && user.role === Role.Customer);
      return customers;
    }
    return [];
  };

  // Calculate totals for txnPoint and winPoint
  const calculateTotals = () => {
    const totalTxnPoint = aggTran.reduce((acc, curr) => acc + curr.transactionPoint, 0);
    const totalWinPoint = aggTran.reduce((acc, curr) => acc + curr.winPoint, 0);
    return { totalTxnPoint, totalWinPoint };
  };

  const { totalTxnPoint, totalWinPoint } = calculateTotals();

  const SafeDownloadButton: React.FC<{
    onClick: () => void;
    children: React.ReactNode;
  }> = DownloadButton as any;

  function PdfDownloadButton({ url }: { url: string | null }) {
    if (!url) return null;

    return (
      <SafeDownloadButton
        onClick={() => {
          const link = document.createElement('a');
          link.href = url;
          link.download = `day2day-report-${format(
            new Date(),
            'dd-MM-yyyy'
          )}.pdf`;
          link.click();
        }}
      >
        <FileText /> Download Report
      </SafeDownloadButton>
    );
  }

  return (
    <Container>
      <PageHeading>Day 2 Day Report</PageHeading>

      <TableWrapper>
        <GenericDatePicker
          label='Start Date'
          value={startDate}
          onChange={(date) => setStartDate(date)}
        />
        <GenericDatePicker
          label='End Date'
          value={endDate}
          onChange={(date) => setEndDate(date)}
        />
        {/* SuperAdmin selects Admin */}
        {role === Role.SuperAdmin && (
          <GenericDropdown
            label="Select Admin"
            options={[
              { value: '0', label: 'Select All' },
              ...getFilteredUsers(Role.Admin, null)?.map((admin) => ({
                value: admin.id.toString(),
                label: admin.name,
              }))
            ]}
            onChange={(value) => {
              if (value === '0') {
                setSelectedAdmin({ id: '0', label: 'Select All' });
              } else {
                setSelectedAdmin(users?.find((user) => user?.id?.toString() === value))
              }
            }}
            value={selectedAdmin?.id?.toString()}
          />
        )}

        {/* Admin selects Agent */}
        {(role === Role.SuperAdmin || role === Role.Admin) && (
          <GenericDropdown
            label="Select Agent"
            options={[
              { value: '0', label: 'Select All' },
              ...getFilteredUsers(Role.Agent, selectedAdmin)?.map((agent) => ({
                value: agent.id.toString(),
                label: agent.name,
              }))
            ]}
            onChange={(value) => {
              if (value === '0') {
                setSelectedAgent({ id: '0', label: 'Select All' });
              } else {
                setSelectedAgent(selectedAdmin
                  ? selectedAdmin.children?.find((user: any) => user?.id?.toString() === value)
                  : users.find((user) => user?.id?.toString() === value))
              }
            }}
            value={selectedAgent?.id?.toString()}
          />
        )}

        {/* Agent selects Customer */}
        {(role === Role.SuperAdmin || role === Role.Admin || role === Role.Agent) && (
          <GenericDropdown
            label="Select Customer"
            options={[
              { value: '0', label: 'Select All' },
              ...getFilteredUsers(Role.Customer, selectedAgent)?.map((agent) => ({
                value: agent.id.toString(),
                label: agent.name,
              }))
            ]}
            onChange={(value) => {
              if (value === '0') {
                setSelectedCustomer({ id: '0', label: 'Select All' })
              } else {
                setSelectedCustomer(selectedAgent
                  ? selectedAgent.children?.find((user: any) => user?.id?.toString() === value)
                  : users.find((user) => user?.id?.toString() === value))
              }

            }}
            value={selectedCustomer?.id?.toString()}
          />
        )}

        <GenericDropdown
          label="Select Game"
          options={[
            { value: '0', label: 'Select All' },
            ...Object.keys(groupedGameConfigs).map((gameName) => ({
              value: gameName,
              label: gameName,
            }))
          ]}
          onChange={(gameName) => {
            setSelectedGame(gameName);
            //  setResult({...result, gameDTO: gameConfigs.find(x => x?.game?.name === gameName)?.game})
          }}
          value={selectedGame}
        />

        {/* Select Result Time */}
        <GenericDropdown
          label="Select Result Time"
          options={resultTimeOptions}
          onChange={(resultTime) => {
            setSelectedResultTime(resultTime);
            // setResult({...result, resultTime: gameConfigs.find(x => x.resultTime.id.toString() === resultTime)?.resultTime})
          }}
          value={selectedResultTime}
        />

        {/* Select Game Type */}
        {
          <GenericDropdown
            label="Select Game Type"
            options={gameTypeOptions}
            onChange={(gameType) => setSelectedGameType(gameType)}
            value={selectedGameType}
          />
        }

        <GenericButton
          label='Fetch'
          onClick={() => handleFetchTransactions()}
        />

        {aggTran && aggTran.length > 0 && (
          <BlobProvider
            document={
              (() => {
                // Define column totals in the outer scope so it's accessible in the summary prop
                const columnTotals: Record<string, number> = {};
                const numericFields = ['transactionPoint', 'winPoint', 'customerCommission', 'agentCommission', 'adminCommission', 'totalCommission'];

                // Initialize totals
                numericFields.forEach(field => {
                  columnTotals[field] = 0;
                });

                const data = (() => {
                  // Process and sort data
                  const processedData = [...aggTran].sort((a, b) => {
                    // First sort by game name
                    const gameCompare = (a.game || '').localeCompare(b.game || '');
                    if (gameCompare !== 0) return gameCompare;

                    // Then by result time
                    const timeCompare = (a.resultTime || '').localeCompare(b.resultTime || '');
                    if (timeCompare !== 0) return timeCompare;

                    // Finally by date as a fallback
                    const dateA = new Date(a.date).getTime();
                    const dateB = new Date(b.date).getTime();
                    return dateA - dateB;
                  }).map(row => {
                    // Format date - ensure it's a valid date string before formatting
                    const dateValue = (() => {
                      if (!row.date) return 'N/A';

                      try {
                        // If it's a timestamp (number)
                        if (typeof row.date === 'number') {
                          const date = new Date(row.date);
                          return isNaN(date.getTime()) ? 'N/A' : format(date, 'dd-MM-yyyy');
                        }

                        // If it's a string
                        const dateStr = String(row.date).trim();
                        if (!dateStr) return 'N/A';

                        // Try parsing as ISO string first
                        let date = new Date(dateStr);

                        // If that fails, try other formats
                        if (isNaN(date.getTime())) {
                          // Try DD/MM/YYYY format
                          const slashParts = dateStr.split('/');
                          if (slashParts.length === 3) {
                            const [day, month, year] = slashParts.map(Number);
                            if (day && month && year) {
                              date = new Date(year, month - 1, day);
                            }
                          }

                          // Try YYYY-MM-DD format
                          if (isNaN(date.getTime()) && dateStr.includes('-')) {
                            const dashParts = dateStr.split('-');
                            if (dashParts.length === 3) {
                              const [year, month, day] = dashParts.map(Number);
                              if (year && month && day) {
                                date = new Date(year, month - 1, day);
                              }
                            }
                          }
                        }

                        // If we have a valid date, format it
                        if (!isNaN(date.getTime())) {
                          return format(date, 'dd-MM-yyyy');
                        }

                        console.warn('Could not parse date:', row.date);
                        return 'N/A';

                      } catch (e) {
                        console.error('Error formatting date:', e);
                        return 'N/A';
                      }
                    })();

                    // Format numeric values and calculate totals
                    const formattedRow: any = { ...row, date: dateValue };

                    numericFields.forEach(field => {
                      if (field in row) {
                        const numValue = Number((row as any)[field]) || 0;
                        formattedRow[field] = numValue.toFixed(2);
                        columnTotals[field] += numValue;
                      }
                    });

                    // Calculate profit/loss
                    formattedRow.profit = (Number(row.winPoint || 0) - Number(row.transactionPoint || 0)).toFixed(2);

                    return formattedRow;
                  });

                  // Add totals row
                  if (processedData.length > 0) {
                    const totalsRow: any = { isTotalRow: true, date: 'Total' };
                    numericFields.forEach(field => {
                      if (columnTotals[field] !== undefined) {
                        totalsRow[field] = columnTotals[field].toFixed(2);
                      }
                    });
                    // Calculate total profit/loss
                    totalsRow.profit = (columnTotals.winPoint - columnTotals.transactionPoint).toFixed(2);
                    processedData.push(totalsRow);
                  }

                  return processedData;
                })();

                return (
                  <GenericReportPDF
                    title="Day to Day Transaction Report"
                    reportType="Transaction Report"
                    gameDetails={{
                      gameName: selectedGame !== '0' ? selectedGame : 'All Games',
                      resultTime: selectedResultTime !== '0' ? resultTimeOptions.find(rt => rt.value === selectedResultTime)?.label : 'All Times',
                      gameType: selectedGameType || 'All Types'
                    }}
                    dateRange={{
                      startDate: startDate || undefined,
                      endDate: endDate || undefined
                    }}
                    columns={getVisibleCommissionColumns().filter((col) => col.field !== 'date')}
                    data={data}
                    summary={{
                      totalBetAmount: columnTotals.transactionPoint || 0,
                      totalWinAmount: columnTotals.winPoint || 0,
                      profitLoss: (columnTotals.winPoint || 0) - (columnTotals.transactionPoint || 0),
                      agentCommission: columnTotals.agentCommission || 0,
                      adminCommission: columnTotals.adminCommission || 0
                    }}
                  />
                );
              })()
            }
          >
            {({ url }) => (
              <PdfDownloadButton url={url} />
            )}
          </BlobProvider>
        )}


        <ReportTableSection>
          <div style={{ marginBottom: '1rem' }}>
            <GenericTable
              columns={getVisibleCommissionColumns()}
              data={[...aggTran].sort((a, b) => {
                // First sort by game name
                const gameCompare = (a.game || '').localeCompare(b.game || '');
                if (gameCompare !== 0) return gameCompare;

                // Then by result time
                const timeCompare = (a.resultTime || '').localeCompare(b.resultTime || '');
                if (timeCompare !== 0) return timeCompare;

                // Finally by date as a fallback
                const dateA = new Date(a.date).getTime();
                const dateB = new Date(b.date).getTime();
                return dateA - dateB;
              })}
              actions={false}
              pagination={false}
            />
          </div>

          {/* Column Totals */}
          {aggTran.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1px',
              backgroundColor: '#dee2e6',
              border: '1px solid #dee2e6',
              marginTop: '-1px',
              overflow: 'visible',
              minWidth: '100%'
            }}>
              {getVisibleCommissionColumns().map((column, index) => {
                // Calculate column total
                let columnTotal = 0;
                let showTotal = false;

                // Handle regular fields with direct values
                if (column.field === 'transactionPoint' || column.field === 'winPoint' ||
                  column.field === 'customerCommission' || column.field === 'agentCommission' ||
                  column.field === 'adminCommission') {

                  showTotal = true;

                  aggTran.forEach(row => {
                    // Only include in total if user has permission to see this column
                    if (column.field === 'customerCommission' &&
                      !(role === Role.Customer || role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin)) {
                      return;
                    }

                    if (column.field === 'agentCommission' &&
                      !(role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin)) {
                      return;
                    }

                    if (column.field === 'adminCommission' &&
                      !(role === Role.Admin || role === Role.SuperAdmin)) {
                      return;
                    }

                    const value = Number(row[column.field as keyof typeof row] || 0);
                    columnTotal += isNaN(value) ? 0 : value;
                  });
                }
                // Handle profit field
                else if (column.field === 'profit') {
                  showTotal = true;
                  // Calculate profit based on user role
                  if (role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) {
                    columnTotal = totalTxnPoint - totalWinPoint;
                  } else {
                    columnTotal = totalWinPoint - totalTxnPoint;
                  }
                }
                // Handle total commission calculation
                else if (column.field === 'totalCommission') {
                  showTotal = true;
                  let totalCommission = 0;

                  // Calculate total commission by summing up all visible commissions for each row
                  aggTran.forEach(row => {
                    let rowTotal = 0;
                    const rowData = row as any; // Type assertion to access dynamic properties

                    // Start with profit (winPoint - transactionPoint)
                    rowTotal += (Number(rowData.winPoint) || 0) - (Number(rowData.transactionPoint) || 0);

                    // Add customer commission if visible
                    if (role === Role.Customer || role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) {
                      rowTotal += Number(rowData.customerCommission) || 0;
                    }

                    // Add agent commission if visible
                    if ((role === Role.Agent || role === Role.Admin || role === Role.SuperAdmin) && rowData.agentCommission) {
                      rowTotal += Number(rowData.agentCommission) || 0;
                    }

                    // Add admin commission if visible
                    if ((role === Role.Admin || role === Role.SuperAdmin) && rowData.adminCommission) {
                      rowTotal += Number(rowData.adminCommission) || 0;
                    }

                    totalCommission += rowTotal;
                  });

                  columnTotal = totalCommission;
                }

                return (
                  column.field !== 'date' && column.field !== 'game' && column.field !== 'resultTime'
                  && column.field !== 'customerName'
                  && column.field !== 'agentName'
                  && column.field !== 'adminName' && (
                    <div
                      key={index}
                      style={{
                        backgroundColor: 'white',
                        padding: '8px',
                        fontWeight: 'bold',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: index === 0 ? 'flex-start' : 'flex-end',
                        gap: '4px'
                      }}
                    >
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#6c757d',
                        wordBreak: 'break-word',
                        width: '100%',
                        textAlign: index === 0 ? 'left' : 'right'
                      }}>
                        {column.header}
                      </div>
                      <div>
                        {index === 0
                          ? 'Total:'
                          : showTotal
                            ? (
                              <span style={{
                                color: columnTotal >= 0 ? 'green' : 'red',
                                fontWeight: 'bold'
                              }}>
                                {columnTotal.toFixed(2)}
                              </span>
                            )
                            : ''
                        }
                      </div>
                    </div>
                  )
                )
              })}
            </div>
          )}
        </ReportTableSection>
      </TableWrapper>

      {/* Total Section */}
      {/* <TotalSection>
        <div><strong>Total Transaction Amount:</strong> {totalTxnPoint}</div>
        <div><strong>Total Win Amount:</strong> {totalWinPoint}</div>
        <div>
          <strong>Profit/Loss: </strong>
          {(() => {
            const profitLoss = totalWinPoint - totalTxnPoint;
            const profitLossStyle = profitLoss >= 0 ? { color: 'green' } : { color: 'red' };
            return (
              <span style={profitLossStyle}>
                {profitLoss >= 0 ? `+${profitLoss}` : profitLoss}
              </span>
            );
          })()}
        </div>
      </TotalSection> */}

    </Container>
  );
};

export default Day2DayReport;
